import "./trax-grid.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	GridColumnProps,
	GridCustomRowProps,
	GridDetailRowProps,
	GridPagerSettings,
	GridProps,
} from "@tql/ui-components-react/kendo/grid";
import {
	Grid,
	GridColumn as Column,
} from "@tql/ui-components-react/kendo/grid";
import { searchSm } from "@tql/ui-components-react/untitled/icons";
import { clsx } from "clsx";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";

import { DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS } from "~/shared/utils/default-grid-pager-settings.ts";

import { LoaderCell } from "./loader-cell/loader-cell.tsx";

// Hoisted default prop objects so we don't allocate new references every
// render — Kendo's Grid otherwise treats them as "changed" each time and
// re-runs internal reconciliation work.
const DEFAULT_SELECTABLE = {
	enabled: true,
	drag: false,
	cell: false,
	mode: "multiple",
} as const;

const DEFAULT_PAGEABLE: GridPagerSettings =
	DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS;

export type TraxGridProps = GridProps & {
	readonly columns: GridColumnProps[];
	readonly className?: string;
	readonly maxHeight?: string;
	readonly loading?: boolean;
	readonly loadingRows?: number;
	readonly exactLoadingRows?: number;
	readonly editable?: boolean;
};

/* Skeleton rows rendered while loading. Generous on purpose: enough to fill any
   single-viewport grid at 40px/row (~2000px tall). The grid clips the excess
   (see `.trax-grid-loading` in trax-grid.css), so this never needs per-grid
   measurement. */
const SKELETON_LOADING_ROWS = 50;

export const TraxGrid = ({
	columns,
	className = "",
	maxHeight,
	loadingRows = 5,
	exactLoadingRows,
	editable,
	loading,
	detail,
	rows,
	...gridProps
}: TraxGridProps) => {
	const combinedClassName = clsx(
		"trax-grid",
		className,
		loading && "trax-grid-loading",
	);

	// Render enough skeleton rows to fill the grid while loading. Grids resolve
	// their height differently (and some use variable-height rows), so instead of
	// measuring each one, render a generous fixed count and let the grid's own
	// overflow clip the excess (see `.trax-grid-loading` in trax-grid.css, which
	// also pins skeleton rows to a consistent height). The caller's `loadingRows`
	// acts as a minimum. When a grid needs a short, fixed skeleton (e.g. compact
	// dashboard cards), `exactLoadingRows` renders exactly that many rows and
	// bypasses the generous fill.
	const loadingRowCount =
		exactLoadingRows ?? Math.max(loadingRows, SKELETON_LOADING_ROWS);

	const loadingData = useMemo(
		() =>
			Array.from({ length: loadingRowCount }, (_, index) => ({
				id: `loading-${index}`,
			})),
		[loadingRowCount],
	);

	// Inject search icons into this grid's filter cells only.
	// Scoped to the grid's own container (via ref) and only watches the
	// header subtree, where filter cells live. This avoids the previous
	// pattern of every TraxGrid instance running a document-wide query on
	// every DOM mutation (which made sorting/column-reorder very slow once a
	// lot of rows were rendered).
	const gridContainerRef = useRef<HTMLDivElement>(null);

	const injectSearchIcons = useCallback((root: ParentNode) => {
		const filterCells = root.querySelectorAll(".k-filtercell");
		if (filterCells.length === 0) return;

		for (const cell of filterCells) {
			if (cell.querySelector(":scope > .search-icon")) continue;

			const iconContainer = document.createElement("span");
			iconContainer.className = "search-icon";

			const reactRoot = createRoot(iconContainer);
			reactRoot.render(<SvgIcon icon={searchSm} />);

			// Track the React root on the element so we can unmount it on
			// cleanup and avoid leaking roots across re-renders.
			(iconContainer as HTMLSpanElement & { __reactRoot?: Root }).__reactRoot =
				reactRoot;

			cell.append(iconContainer);
		}
	}, []);

	useEffect(() => {
		const container = gridContainerRef.current;
		if (!container) return;

		let rafId = 0;
		const schedule = () => {
			if (rafId) return;
			rafId = requestAnimationFrame(() => {
				rafId = 0;
				injectSearchIcons(container);
			});
		};

		// Initial pass.
		schedule();

		// Only react to mutations that actually add element nodes (filter row
		// re-render, column reorder, etc.). Ignore text/attribute changes that
		// happen on every cell during sort/scroll.
		const observer = new MutationObserver((mutations) => {
			for (const m of mutations) {
				if (m.addedNodes.length === 0) continue;
				for (const node of m.addedNodes) {
					if (node.nodeType !== Node.ELEMENT_NODE) continue;
					const el = node as Element;
					if (
						el.classList?.contains("k-filtercell") ||
						el.querySelector?.(".k-filtercell")
					) {
						schedule();
						return;
					}
				}
			}
		});

		// Filter cells live in the header — observe only that part of the grid
		// instead of the entire grid (which includes the data rows).
		const headerEl = container.querySelector(".k-grid-header") ?? container;
		observer.observe(headerEl, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
			if (rafId) cancelAnimationFrame(rafId);
			// Unmount any React roots we created so we don't leak them. Defer
			// unmount via queueMicrotask: React 18 warns when a root is
			// synchronously unmounted while a render is in progress (which can
			// happen when this cleanup fires during the parent's commit phase).
			const icons = container.querySelectorAll<HTMLSpanElement>(".search-icon");
			for (const icon of icons) {
				const withRoot = icon as HTMLSpanElement & { __reactRoot?: Root };
				const reactRoot = withRoot.__reactRoot;
				if (reactRoot) {
					queueMicrotask(() => reactRoot.unmount());
				}
				icon.remove();
			}
		};
	}, [injectSearchIcons]);

	const CustomRow = useMemo(() => {
		const RowComponent = rows?.data ?? "tr";

		return function TraxGridCustomRow(props: GridCustomRowProps) {
			const { trProps, children } = props;
			const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
				if (
					(e.key === " " || e.key === "Enter") &&
					((e.target as HTMLElement).tagName === "INPUT" ||
						(e.target as HTMLElement).tagName === "TEXTAREA")
				) {
					e.stopPropagation();
				}
				trProps?.onKeyDown?.(e);
			};

			if (RowComponent === "tr") {
				return (
					<tr {...trProps} onKeyDown={handleKeyDown}>
						{children}
					</tr>
				);
			}

			const CustomRowComponent = RowComponent;
			return (
				<CustomRowComponent
					{...props}
					trProps={{ ...trProps, onKeyDown: handleKeyDown }}
				/>
			);
		};
	}, [rows?.data]);

	const WrappedDetail = useMemo(() => {
		if (!detail) return undefined;
		const Detail = detail;

		return function TraxGridDetailWrapper(props: GridDetailRowProps) {
			return (
				<div
					onKeyDown={(e) => {
						if (
							(e.key === " " || e.key === "Enter") &&
							((e.target as HTMLElement).tagName === "INPUT" ||
								(e.target as HTMLElement).tagName === "TEXTAREA")
						) {
							e.stopPropagation();
						}
					}}
					role={"presentation"}
				>
					<Detail {...props} />
				</div>
			);
		};
	}, [detail]);

	// Stable reference for the `rows` prop so a re-render of the parent
	// (e.g. on sort/scroll) doesn't hand Kendo a "new" rows config each time.
	const rowsProp = useMemo(
		() => ({ ...rows, data: CustomRow }),
		[rows, CustomRow],
	);

	const basePageable = gridProps.pageable ?? DEFAULT_PAGEABLE;
	// While the grid is loading (skeleton), hide the page-size selector so the
	// user isn't presented a page size they can't actually change yet (page
	// changes are guarded while the persisted preference is still loading). The
	// selector reappears with the correct value once loading completes.
	const effectivePageable =
		loading && typeof basePageable === "object"
			? { ...basePageable, pageSizes: false }
			: basePageable;

	return (
		<div
			ref={gridContainerRef}
			className={"trax-grid-container"}
			style={{ display: "contents" }}
		>
			<Grid
				autoProcessData={true}
				dataItemKey={gridProps.dataItemKey ?? "id"}
				defaultSkip={gridProps.defaultSkip ?? 0}
				defaultTake={gridProps.defaultTake ?? 25}
				editable={editable ?? true}
				navigatable={true}
				pageSize={gridProps.pageSize}
				resizable={true}
				selectable={DEFAULT_SELECTABLE}
				sortable={true}
				{...gridProps}
				cells={loading ? { data: LoaderCell } : gridProps.cells}
				className={combinedClassName}
				data={loading ? loadingData : gridProps.data}
				detail={loading ? undefined : WrappedDetail}
				detailExpand={loading ? undefined : gridProps.detailExpand}
				onDetailExpandChange={
					loading ? undefined : gridProps.onDetailExpandChange
				}
				pageable={effectivePageable}
				rows={rowsProp}
				skip={loading ? 0 : gridProps.skip}
				style={
					{
						"--trax-grid-max-height": maxHeight,
						...gridProps.style,
					} as React.CSSProperties
				}
			>
				{columns.map((col, index) => {
					const stableId =
						(col.field ? String(col.field) : "") ||
						(col.title ? String(col.title) : "") ||
						`col-${index}`;
					return (
						<Column
							{...col}
							key={stableId}
							cells={loading ? { ...col.cells, data: LoaderCell } : col.cells}
							id={stableId}
						/>
					);
				})}
			</Grid>
		</div>
	);
};
